import React, { useMemo } from 'react'
import classNames from 'classnames'
import { Table } from '@winnin/winnin_ui.components.table'
import { TooltipWithPortal } from '@winnin/winnin_ui.components.tooltip-with-portal'

import { BaseSkeleton } from 'components/BaseSkeleton'
import FormatedTableCell from 'components/FormatedTableCell'
import GenerateWinninAIReportButton from 'components/GenerateWinninAIReportButton'
import { useTranslate } from 'lib/tolgee'
import { TerritoryTitleTableCell, Favorite } from 'modules/territory'
import { FeatureFlagsStore } from 'stores'
import { humanizeLargeNumber, TOLGEE_GLOBALS } from 'utils'

import { RelevanceTableCellLazy, RelevanceTableCellLazyWithoutRetry } from './RelevanceTableCell'

import './index.scss'

const TOLGEE_PREFIX = 'TerritoriesTable'

type TerritoriesTableProps = {
  territories: any[]
  sortField: string
  sortOrder: string
  handleSort: (field: string) => void
  allowSortByVideos?: boolean
  isZaiReportColumnVisible?: boolean
  isRelevanceRetryVisible?: boolean
}

export function TerritoriesTable({
  territories,
  sortField,
  sortOrder,
  handleSort,
  allowSortByVideos,
  isZaiReportColumnVisible = true,
  isRelevanceRetryVisible = true,
}: Readonly<TerritoriesTableProps>) {
  const t = useTranslate()

  const headers = useMemo(
    () => [
      {
        label: '',
        key: 'favorite',
        component: FavoriteTableCell,
        isSortable: false,
      },
      {
        label: t(TOLGEE_GLOBALS.topic),
        key: 'name',
        isTitle: true,
        component: TerritoryTitleTableCell,
      },
      {
        label: t(TOLGEE_GLOBALS.relevance),
        key: 'relevance',
        component: isRelevanceRetryVisible ? RelevanceTableCellLazy : RelevanceTableCellLazyWithoutRetry,
        isSortable: false,
      },
      {
        label: t(TOLGEE_GLOBALS.engPerVideo),
        key: 'avgEngagement',
        component: AvgEngagementTableCell,
        isSortable: true,
      },
      {
        label: t(TOLGEE_GLOBALS.viewsPerVideo),
        key: 'avgViews',
        component: AvgViewsTableCell,
        isSortable: true,
      },
      {
        label: t(TOLGEE_GLOBALS.videos),
        key: allowSortByVideos ? 'totalVideos' : 'totalPosts',
        component: VideosTableCell,
        isSortable: allowSortByVideos,
      },
      ...(isZaiReportColumnVisible
        ? [
            {
              label: '',
              key: 'zai',
              component: ZaiTableCell,
              isSortable: false,
            },
          ]
        : []),
    ],
    [allowSortByVideos, isRelevanceRetryVisible, isZaiReportColumnVisible, t]
  )

  return (
    <div className={classNames('TerritoriesTable', { 'TerritoriesTable--withoutReport': !isZaiReportColumnVisible })}>
      <Table
        headers={headers}
        data={territories}
        currentActive={sortField}
        customSort={handleSort}
        isCustomAsc={sortOrder === 'asc'}
        className="TerritoriesTable-component"
      />
    </div>
  )
}

function FavoriteTableCell({ row }) {
  const [entityType] = row?.territoryId?.split('.') ?? []
  if (entityType === 'industry_topic') {
    return null
  }
  return (
    <div className={classNames(['favoriteContainer', { isActive: row?.isFollowed }])}>
      <Favorite
        initialSaveState={row?.isFollowed}
        territoryId={row?.territoryId}
        territoryName={row?.name}
        territoryType={entityType}
        size="sm"
      />
    </div>
  )
}

function MetricLoadingTableCell() {
  return <BaseSkeleton height={10} borderRadius={16} width={60} />
}

function VideosTableCell({ value, row }) {
  if (row?.isMetricsLoading) return <MetricLoadingTableCell />
  if (row?.isMetricsUnavailable) return null
  return <FormatedTableCell value={value} />
}

function NumberWithVariationTableCell({ number, variation, dateRange }) {
  const t = useTranslate()
  const isHiddenVariationTerritoriesEnabled = FeatureFlagsStore.isFeatureFlagEnabled(
    'isHiddenVariationTerritoriesEnabled'
  )

  if (isHiddenVariationTerritoriesEnabled) {
    return <div className="NumberWithVariationTableCell">{humanizeLargeNumber(number, 2)}</div>
  }

  return (
    <div className="NumberWithVariationTableCell">
      {humanizeLargeNumber(number, 2)}
      {(variation > 0 || variation < 0) && (
        <div
          className={`NumberWithVariationTableCell-variation ${
            variation > 0
              ? 'NumberWithVariationTableCell-variation-positive'
              : 'NumberWithVariationTableCell-variation-negative'
          }`}
        >
          {variation > 0 && <>+</>}

          <TooltipWithPortal
            tooltipMessage={t({
              defaultValue: 'Valor comparativo referente aos últimos {dateRange} meses',
              key: `${TOLGEE_PREFIX}-variationTooltip`,
              parameters: { dateRange },
            })}
          >
            {`${(variation * 100).toFixed(1)}%`}
          </TooltipWithPortal>
        </div>
      )}
    </div>
  )
}

function AvgViewsTableCell({ value, row }) {
  if (row?.isMetricsLoading) return <MetricLoadingTableCell />
  if (row?.isMetricsUnavailable) return null
  return <NumberWithVariationTableCell number={value} variation={row?.avgViewsVariation} dateRange={row?.dateRange} />
}

function AvgEngagementTableCell({ value, row }) {
  if (row?.isMetricsLoading) return <MetricLoadingTableCell />
  if (row?.isMetricsUnavailable) return null
  return (
    <NumberWithVariationTableCell number={value} variation={row?.avgEngagementVariation} dateRange={row?.dateRange} />
  )
}

function ZaiTableCell({ row }) {
  const [entityType, id] = row?.territoryId?.split('.') ?? []
  if (entityType === 'industry_topic') {
    return null
  }
  return (
    <GenerateWinninAIReportButton
      id={id}
      entity={entityType}
      analyticsTrackerPageTitle={row?.name}
      zaiBtnConfig={{
        appearance: 'link',
        className: 'GenerateWinninAIReportButton-text',
        iconClassName: 'GenerateWinninAIReportButton-icon2',
      }}
      isDisabled={false}
    />
  )
}
